import * as React from 'react';

import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import { api } from '@api/reduxApi';
import { useGetEnabledIntegrationsQuery } from '@components/CandidatesApp/CandidatesIndex/api';
import { Button, DropdownLink, Popper, Text } from '@components/common';
import { Grid } from '@components/common/Grid';
import { useRecruitingRequest } from '@components/StudiesApp/components/StudyDraft/hooks/useRecruitingRequest';
import { AddPeopleSVG, ChipSVG, ExternalCandidatesSVG, ImportSVG, LinkSVG, RepositorySVG } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';
import { useDisabledFeatures } from '@hooks/useDisabledFeatures';

import { ADD_EXISTING, ADD_MANUAL, INVITE_VIA_LINK, StudyAction } from './ACTIONS';

const Title = ({ children }) => <h3 className='text-lg font-bold'>{children}</h3>;

const Description = ({ children }) => <p className='mb-6 text-sm text-gray-500'>{children}</p>;

interface OptionProps {
  className?: string;
}

const Option: React.FC<React.PropsWithChildren<OptionProps>> = ({ className = '', children }) => (
  <div
    className={cn(className, 'desktop:p-6 flex flex-col justify-start rounded-md border border-gray-200 bg-white p-4')}
  >
    {children}
  </div>
);

interface Props {
  setMode: (mode: StudyAction | null) => void;
  study: Study;
  setAddCandidatesDefaultStudyLimit: (studyLimit: StudyLimit | null) => void;
  studyLimits?: StudyLimit[];
  demoMode?: boolean;
}

type Options = {
  title: string;
  check_for_disabled_features?: ToggleableFeature[];
  description: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
  renderCTA: (
    props: Props & { setMode: (mode: StudyAction | null) => void; handleNew: () => void }
  ) => React.JSX.Element;
}[];

const OPTIONS: Options = [
  {
    title: 'Invite via link',
    check_for_disabled_features: ['adding_candidates'],
    description: 'Send or share a link with the audience you want to participate.',
    icon: LinkSVG,
    renderCTA: ({ setMode, study }) => (
      <Tooltip
        className='w-full'
        isDisabled={study.state !== 'pending'}
        content='Study requires approval before you can invite.'
      >
        <Button
          className='self-end'
          disabled={study.state !== 'active'}
          trackEvent='clicked_invite_via_link'
          trackProps={{ page: 'participants_zds' }}
          onClick={() => setMode(INVITE_VIA_LINK)}
          fullWidth
        >
          Select link
        </Button>
      </Tooltip>
    )
  },
  {
    title: 'Import list',
    check_for_disabled_features: ['adding_candidates', 'adding_candidates_via_csv'],
    description: 'Upload a CSV file with candidate information to quickly add multiple participants at once.',
    icon: ImportSVG,
    renderCTA: ({ study }) => (
      <Button
        className='self-end'
        trackEvent='clicked_csv_import'
        trackProps={{ page: 'participants_zds' }}
        href={`/customer_imports/new?study_id=${study.id}`}
        fullWidth
      >
        Import CSV
      </Button>
    )
  },
  {
    title: 'Recruit externally',
    description: 'Recruit from Respondent’s database of 3 million qualified B2B and B2C participants.',
    icon: ExternalCandidatesSVG,
    renderCTA: ({ study, handleNew }) => (
      <Tooltip
        className='w-full'
        isDisabled={study.state !== 'pending'}
        content='Study requires approval before you can invite.'
      >
        <Button
          trackEvent='clicked_add_external_candidates'
          trackProps={{ page: 'participants_zds' }}
          onClick={handleNew}
          className='self-end'
          fullWidth
        >
          Create request
        </Button>
      </Tooltip>
    )
  },
  {
    title: 'Browse your candidates',
    description: 'Filter through the existing candidates already in your Great Question database.',
    icon: RepositorySVG,
    renderCTA: ({ studyLimits, setMode, setAddCandidatesDefaultStudyLimit }) =>
      studyLimits && studyLimits.length > 0 ? (
        <Popper
          zIndex={50}
          syncWidth
          renderOnBodyRoot={false}
          closeOnClickOutside
          placement='bottom-start'
          offset={[0, 8]}
          content={({ closePopper }) => (
            <div
              className='rounded-md border border-gray-200 bg-white py-2 shadow-lg ring-1 ring-black ring-opacity-5'
              role='menu'
              aria-orientation='vertical'
              aria-labelledby='options-menu'
            >
              <DropdownLink
                aria-label='Select existing candidates'
                trackEvent='clicked_add_existing_candidates'
                trackProps={{ page: 'participants_zds' }}
                onClick={() => {
                  setMode(ADD_EXISTING);
                  closePopper();
                }}
              >
                Select existing candidates
              </DropdownLink>
              <hr className='my-2' />
              {studyLimits.map((limit) => (
                <DropdownLink
                  aria-label={`Add from ${limit.name ? limit.name : `Segment #${limit.order}`}`}
                  key={limit.id}
                  trackEvent='clicked_add_candidates_from_segment'
                  trackProps={{ page: 'participants_zds' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddCandidatesDefaultStudyLimit(limit);
                    setMode(ADD_EXISTING);
                    closePopper();
                  }}
                >
                  Add from {limit.name ? limit.name : `Segment #${limit.order}`}
                </DropdownLink>
              ))}
            </div>
          )}
        >
          <Button
            trackEvent='clicked_add_existing_candidates'
            trackProps={{ page: 'participants_zds' }}
            aria-label='Browse candidates'
            iconSuffix='chevronDown'
            fullWidth
          >
            Browse candidates
          </Button>
        </Popper>
      ) : (
        <Button
          className='xx-study-add-existing self-end'
          trackEvent='clicked_add_existing_candidates'
          dataTestId='add-existing-candidates-button'
          trackProps={{ page: 'participants_zds' }}
          fullWidth
          onClick={() => setMode(ADD_EXISTING)}
        >
          Browse candidates
        </Button>
      )
  },
  {
    title: 'Integrate your CRM',
    description: 'Integrate with Salesforce, Snowflake or Databricks to keep candidate data in sync.',
    icon: ChipSVG,
    renderCTA: () => (
      <Button
        target='_blank'
        className='self-end'
        trackEvent='clicked_view_integrations'
        trackProps={{ page: 'participants_zds' }}
        href='/user/connected_accounts'
        iconSuffix='externalLink'
        fullWidth
      >
        View integrations
      </Button>
    )
  },
  {
    title: 'Add manually',
    check_for_disabled_features: ['adding_candidates'],
    description: 'Add participants individually with detailed information.',
    icon: AddPeopleSVG,
    renderCTA: ({ setMode }) => (
      <Button
        className='self-end'
        fullWidth
        trackEvent='clicked_add_manually'
        trackProps={{ page: 'participants_zds' }}
        onClick={() => setMode(ADD_MANUAL)}
      >
        Add participant
      </Button>
    )
  }
];

export const ZDS: React.FC<React.PropsWithChildren<Props>> = ({
  setMode,
  study,
  setAddCandidatesDefaultStudyLimit,
  studyLimits,
  demoMode = false
}) => {
  const hasExternalCandidatesAccess = study.external_candidates_enabled;

  const { data: integrations = [] } = useGetEnabledIntegrationsQuery();

  const { data: countData } = api.useGetCandidatesStatsQuery({ items: 1 });

  const { disabledFeatures } = useDisabledFeatures();

  const options = OPTIONS.filter((option) => {
    if (option.check_for_disabled_features) {
      return option.check_for_disabled_features.every((feature) => !disabledFeatures[feature]);
    }

    if (demoMode && (option.title === 'Invite via link' || option.title === 'Recruit externally')) {
      return false;
    }

    if (option.title === 'Integrate your CRM') {
      return ['salesforce', 'snowflake', 'databricks'].every((integration) => !integrations.includes(integration));
    }

    if (option.title === 'Recruit externally') {
      return hasExternalCandidatesAccess;
    }

    if (option.title === 'Browse your candidates') {
      return countData && countData.count !== 0;
    }

    return true;
  });

  const navigate = useNavigate();

  const handleUpdatedRequest = (newRequest: ExternalCandidatesRequest) => {
    navigate(`/studies/${study.id}/recruitment_requests/${newRequest.id}`);
  };

  const { handleNew } = useRecruitingRequest({
    study: study,
    handleUpdatedRequest,
    skip: true
  });

  return (
    <>
      {!demoMode && (
        <>
          <Text as='h2' className='text-xl font-bold text-gray-700'>
            Participants
          </Text>
          <Text color='gray-500' className='mb-6'>
            Recruit and manage your research participants.
          </Text>
        </>
      )}
      <Grid
        className='desktop:gap-6 w-full gap-4'
        mobile={demoMode ? 1 : 1}
        tablet={demoMode ? 1 : 2}
        desktop={demoMode ? 1 : 2}
        monitor={demoMode ? 1 : 3}
      >
        {options.map(({ title, description, icon: Icon, renderCTA }) => (
          <Option key={title}>
            <div className='mb-6 flex flex-nowrap items-center space-x-3'>
              <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50'>
                <Icon className='h-5 w-5 text-indigo-700' />
              </div>
              <Title>{title}</Title>
            </div>
            <Description>{description}</Description>
            <div className='flex flex-grow items-end'>
              {renderCTA({
                setMode,
                study,
                handleNew,
                setAddCandidatesDefaultStudyLimit,
                studyLimits
              })}
            </div>
          </Option>
        ))}
      </Grid>
    </>
  );
};
